import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db } from '../../firebaseConfig';
import { collection, doc, setDoc, getDoc, getDocs, deleteDoc } from 'firebase/firestore';
import Calendar from 'react-calendar';
import { Typography, Paper, Box, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar, TextField, Autocomplete } from '@mui/material';
import './editDataPage.css';
import { Link } from 'react-router-dom';
import { fr } from 'date-fns/locale';
import ConfirmationDialog from '../../components/confirmationDialog';
import ConfirmationDialogDataNotSaved from '../../components/confirmationDialogDataNoSaved'; // Importez le composant ConfirmationDialogDataNotSaved

import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useMediaQuery from '@mui/material/useMediaQuery';

import { usePrompt, Prompt } from '../../components/usePrompt';


const CalendarPage = () => {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [date, setDate] = useState(new Date());
  const [markedDates, setMarkedDates] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [deletingChild, setDeletingChild] = useState(false); // Nouveau état pour le loader de suppression
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false); // État pour le Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(''); // Message du Snackbar

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newChild, setNewChild] = useState('');

  const [isEditing, setIsEditing] = useState(false);
  const [childDetails, setChildDetails] = useState({ name: '', firstName: '', class: '', userId: '', attendance: [] });
  const [isEdited, setIsEdited] = useState(false);

  // Cache pour optimiser les performances
  const [childrenCache, setChildrenCache] = useState(new Map());
  const [attendanceCache, setAttendanceCache] = useState(new Map());

  const isMobile = useMediaQuery('(max-width:600px)');

  usePrompt("Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter cette page ?", isEdited);

  // Optimisation : mémoriser la fonction fetchAllChildren
  const fetchAllChildren = useCallback(async () => {
    setLoading(true);
    try {
      // Récupère tous les utilisateurs de l'application en une seule fois
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);

      // Filtrer les utilisateurs non archivés
      const activeUsers = usersSnap.docs.filter(userDoc => {
        const userData = userDoc.data();
        return !userData.isArchived;
      });

      // Récupérer tous les enfants en parallèle avec Promise.all pour optimiser
      const allChildrenPromises = activeUsers.map(async (userDoc) => {
        const userId = userDoc.id;
        const childrenRef = collection(db, 'users', userId, 'children');
        const childrenSnap = await getDocs(childrenRef);

        return childrenSnap.docs.map(doc => ({ 
          id: doc.id, 
          userId: userId, 
          ...doc.data() 
        }));
      });

      const allChildrenArrays = await Promise.all(allChildrenPromises);
      let allChildren = allChildrenArrays.flat();

      // Tri optimisé des enfants par nom complet
      allChildren.sort((a, b) => {
        const nameA = `${a.name} ${a.firstName}`.toUpperCase();
        const nameB = `${b.name} ${b.firstName}`.toUpperCase();
        return nameA.localeCompare(nameB);
      });

      // Mise en cache des enfants pour éviter les requêtes répétées
      const newChildrenCache = new Map();
      allChildren.forEach(child => {
        newChildrenCache.set(child.id, child);
      });
      setChildrenCache(newChildrenCache);

      setChildren(allChildren);

      if (allChildren.length > 0) {
        setSelectedChild(allChildren[0].id);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des enfants :', err);
      setError('Erreur lors du chargement des enfants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllChildren();
  }, [fetchAllChildren]);

  // Optimisation : mémoriser la fonction fetchMarkedDates avec cache
  const fetchMarkedDates = useCallback(async (childId) => {
    if (!childId) {
      console.error('Aucun enfant sélectionné.');
      return;
    }

    // Vérifier le cache d'abord
    if (attendanceCache.has(childId)) {
      const cachedData = attendanceCache.get(childId);
      setMarkedDates(cachedData.map(dateStr => new Date(dateStr)));
      return;
    }

    setLoading(true);
    try {
      // Utiliser le cache des enfants pour trouver directement l'userId
      const childData = childrenCache.get(childId);
      if (!childData) {
        console.error('Enfant non trouvé dans le cache.');
        setMarkedDates([]);
        return;
      }

      const childRef = doc(db, 'users', childData.userId, 'children', childId);
      const childSnap = await getDoc(childRef);

      if (childSnap.exists()) {
        const attendance = childSnap.data().attendance || [];
        
        // Mettre en cache les données d'assiduité
        attendanceCache.set(childId, attendance);
        setAttendanceCache(new Map(attendanceCache));
        
        setMarkedDates(attendance.map(dateStr => new Date(dateStr)));
      } else {
        setMarkedDates([]);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des jours marqués :', err);
      setError('Erreur lors du chargement des présences');
    } finally {
      setLoading(false);
    }
  }, [childrenCache, attendanceCache]);

  // Optimisation : mémoriser fetchChildDetails avec le cache
  const fetchChildDetails = useCallback((childId) => {
    try {
      const childData = childrenCache.get(childId);
      if (childData) {
        setChildDetails(childData);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des détails de l\'enfant :', err);
    }
  }, [childrenCache]);

  useEffect(() => {
    if (selectedChild && childrenCache.size > 0) {
      fetchMarkedDates(selectedChild);
      fetchChildDetails(selectedChild);
    }
  }, [selectedChild, fetchMarkedDates, fetchChildDetails]);

  // Optimisation : mémoriser fetchAvailableDates
  const fetchAvailableDates = useCallback(async () => {
    try {
      const docRef = doc(db, 'settings', 'selectableDays');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const days = docSnap.data().days || {};
        setAvailableDates(Object.keys(days).map(dateStr => new Date(dateStr)));
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des jours disponibles :', err);
      setError('Erreur lors du chargement des jours disponibles');
    }
  }, []);
  
  useEffect(() => {
    fetchAvailableDates();
  }, [fetchAvailableDates]);

  // Optimisation : mémoriser handleRefresh pour éviter les re-renders
  const handleRefresh = useCallback(async () => {
    setMarkedDates([]);
    setAvailableDates([]);
    setMessage('');
    setError('');
    
    // Vider les caches pour forcer le rechargement
    attendanceCache.delete(selectedChild);
    setAttendanceCache(new Map(attendanceCache));
    
    await Promise.all([
      fetchMarkedDates(selectedChild),
      fetchAvailableDates()
    ]);
    setIsEdited(false);
  }, [selectedChild, fetchMarkedDates, fetchAvailableDates, attendanceCache]);

  // Optimisation : mémoriser les handlers pour éviter les re-renders
  const handleDateChange = useCallback((newDate) => {
    setDate(newDate);
  }, []);

  const handleChildChange = useCallback((event) => {
    const newChildValue = event.target.value;
    if (isEdited) {
      setNewChild(newChildValue);
      setDialogOpen(true);
    } else {
      setSelectedChild(newChildValue);
      setMessage('');
      fetchChildDetails(newChildValue);
    }
  }, [isEdited, fetchChildDetails]);

  // Nouvelle fonction pour gérer le changement via Autocomplete
  const handleChildAutocompleteChange = useCallback((event, newValue) => {
    const newChildValue = newValue ? newValue.id : '';
    if (isEdited) {
      setNewChild(newChildValue);
      setDialogOpen(true);
    } else {
      setSelectedChild(newChildValue);
      setMessage('');
      if (newChildValue) {
        fetchChildDetails(newChildValue);
      }
    }
  }, [isEdited, fetchChildDetails]);

  const handleConfirmChange = useCallback(() => {
    setDialogOpen(false);
    setSelectedChild(newChild);
    setMessage('');
    setIsEdited(false);
  }, [newChild]);
  
  const handleCancelChange = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleOpenDialog = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleEditSave = useCallback(async () => {
    try {
      const user = auth.currentUser;

      if (!childDetails.name.trim() || !childDetails.firstName.trim() || !childDetails.class.trim()) {
        setError("Veuillez remplir tous les champs.");
        return;
      }

      if (user) {
        const childRef = doc(db, 'users', childDetails.userId, 'children', selectedChild);
        const childEdited = { ...childDetails };
        childEdited.attendance = markedDates.map(date => date.toISOString());
        
        const { userId, ...childCleaned } = childEdited;
        await setDoc(childRef, childCleaned, { merge: true });
        
        // Mettre à jour les caches
        childrenCache.set(selectedChild, childEdited);
        setChildrenCache(new Map(childrenCache));
        attendanceCache.set(selectedChild, childEdited.attendance);
        setAttendanceCache(new Map(attendanceCache));
        
        setChildDetails(childEdited);
        setSnackbarMessage('Les informations de l\'enfant ont été mises à jour avec succès.');
        setOpenSnackbar(true);
        setIsEditing(false);
        setIsEdited(false);
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour des informations de l\'enfant :', err);
      setError('Erreur lors de la sauvegarde');
    }
  }, [childDetails, selectedChild, markedDates, childrenCache, attendanceCache]);

  const toggleDateSelection = useCallback((value) => {
    // Vérification si la date est disponible
    if (availableDates.some(availableDate => availableDate.toDateString() === value.toDateString())) {
      const formattedDate = value.toDateString();
      const newMarkedDates = markedDates.some(markedDate => markedDate.toDateString() === formattedDate)
        ? markedDates.filter(date => date.toDateString() !== formattedDate)
        : [...markedDates, value];

      setIsEdited(true);
      setMarkedDates(newMarkedDates);
      
      // Mettre à jour le cache avec les nouvelles dates
      const attendanceStrings = newMarkedDates.map(date => date.toISOString());
      attendanceCache.set(selectedChild, attendanceStrings);
      setAttendanceCache(new Map(attendanceCache));
    } else {
      alert('Ce jour n\'est pas disponible.');
    }
  }, [availableDates, markedDates, selectedChild, attendanceCache]);

  const handleSelectAllDates = useCallback(() => {
    const currentMonthCalendar = date.getMonth();
    const currentYearCalendar = date.getFullYear();

    const datesToMark = availableDates.filter(availableDate => {
      return availableDate.getMonth() === currentMonthCalendar && availableDate.getFullYear() === currentYearCalendar;
    });

    // Combiner les dates marquées et les nouvelles dates à marquer
    let totalDate = [...markedDates, ...datesToMark];

    // Supprimer les doublons
    const uniqueDates = Array.from(new Set(totalDate.map(date => date.toDateString())))
      .map(dateString => new Date(dateString));

    setIsEdited(true);
    setMarkedDates(uniqueDates);
    
    // Mettre à jour le cache
    const attendanceStrings = uniqueDates.map(date => date.toISOString());
    attendanceCache.set(selectedChild, attendanceStrings);
    setAttendanceCache(new Map(attendanceCache));
  }, [date, availableDates, markedDates, selectedChild, attendanceCache]);

  const handleSelectAllDatesOfYear = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const isAugust = currentMonth === 7;
    const deadline = isAugust ? 26 : 20;
    const isAfterDeadline = today.getDate() > deadline;
    const isCurrentMonthEditable = isAugust && !isAfterDeadline;

    const filteredAvailableDates = availableDates.filter(date => {
      const isPastDate = date < today;
      const isSameMonth = currentMonth === date.getMonth() && !isCurrentMonthEditable;
      const isNextMonth = isAfterDeadline && (date.getMonth() === currentMonth || date.getMonth() === currentMonth + 1);
      return !isPastDate && !isNextMonth && !isSameMonth;
    });

    const totalDate = [...markedDates, ...filteredAvailableDates];
    const uniqueDates = Array.from(new Set(totalDate.map(date => date.toDateString())))
      .map(dateString => new Date(dateString));

    setIsEdited(false);
    setMarkedDates(uniqueDates);
    
    // Mettre à jour le cache
    const attendanceStrings = uniqueDates.map(date => date.toISOString());
    attendanceCache.set(selectedChild, attendanceStrings);
    setAttendanceCache(new Map(attendanceCache));
  }, [availableDates, markedDates, selectedChild, attendanceCache]);


  const handleSave = useCallback(async () => {
    if (!selectedChild) {
      setError('Aucun enfant sélectionné pour enregistrer les jours.');
      return;
    }
    setError('');
    setMessage('');

    try {
      const user = auth.currentUser;
      if (user) {
        const attendanceData = markedDates.map(date => date.toISOString());
        const childRef = doc(db, 'users', childDetails.userId, 'children', selectedChild);
        await setDoc(childRef, { attendance: attendanceData }, { merge: true });
        
        // Mettre à jour le cache
        attendanceCache.set(selectedChild, attendanceData);
        setAttendanceCache(new Map(attendanceCache));
        
        setMessage('Les jours de présence ont été enregistrés avec succès. Pensez à inscrire tous vos enfants.');
        setIsEdited(false);
      }
    } catch (err) {
      setError('Erreur lors de l\'enregistrement des jours de présence.');
      console.error('Erreur lors de l\'enregistrement des jours de présence :', err);
    }
  }, [selectedChild, markedDates, childDetails.userId, attendanceCache]);

  const handleMonthChange = useCallback((activeStartDate) => {
    const firstDayOfMonth = new Date(activeStartDate.getFullYear(), activeStartDate.getMonth(), 1);
    setDate(firstDayOfMonth);
    setMessage('');
    setError('');
  }, []);

  const handleDeleteChild = useCallback(async () => {
    if (selectedChild) {
      setDeletingChild(true); // Activer le loader
      try {
        // Utiliser le cache pour obtenir l'userId directement
        const childData = childrenCache.get(selectedChild);
        if (!childData) {
          console.error('Enfant non trouvé dans le cache.');
          return;
        }

        const childRef = doc(db, 'users', childData.userId, 'children', selectedChild);
        await deleteDoc(childRef);

        // Mettre à jour les caches
        childrenCache.delete(selectedChild);
        setChildrenCache(new Map(childrenCache));
        attendanceCache.delete(selectedChild);
        setAttendanceCache(new Map(attendanceCache));

        // Mise à jour de la liste des enfants
        setChildren(prevChildren => prevChildren.filter(child => child.id !== selectedChild));
        const remainingChildren = children.filter(child => child.id !== selectedChild);
        setSelectedChild(remainingChildren.length > 0 ? remainingChildren[0].id : '');
        
        setSnackbarMessage('L\'enfant a été supprimé avec succès.');
        setOpenSnackbar(true);
        handleCloseDialog();
      } catch (err) {
        console.error('Erreur lors de la suppression de l\'enfant :', err);
        setError('Erreur lors de la suppression de l\'enfant');
      } finally {
        setDeletingChild(false); // Désactiver le loader
      }
    }
  }, [selectedChild, children, childrenCache, attendanceCache, handleCloseDialog]);

  const handleSnackbarClose = useCallback(() => {
    setOpenSnackbar(false);
  }, []);

  // Mémorisation des options pour l'Autocomplete
  const childrenOptions = useMemo(() => 
    children.map(child => ({
      id: child.id,
      label: `${child.name} ${child.firstName}`,
      name: child.name,
      firstName: child.firstName
    })), [children]);

  // Valeur sélectionnée pour l'Autocomplete
  const selectedChildOption = useMemo(() => 
    childrenOptions.find(option => option.id === selectedChild) || null, 
    [childrenOptions, selectedChild]);

  // Optimisation : mémoriser le contenu principal pour éviter les re-renders
  const renderContent = useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (children.length === 0) {
      return (
        <Box sx={{ width: '100%' }}>
          <Alert severity="info">
            Aucun enfant ajouté. Veuillez ajouter au moins un enfant pour l'inscrire à la cantine.
          </Alert>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <div style={{ marginRight: '16px', width: '100%' }}>
            <Autocomplete
              id="select-child"
              options={childrenOptions}
              value={selectedChildOption}
              onChange={handleChildAutocompleteChange}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sélectionnez un enfant"
                  variant="outlined"
                  placeholder="Tapez pour rechercher..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.name} {option.firstName}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="Aucun enfant trouvé"
              clearOnEscape
              openOnFocus
              sx={{ width: '100%' }}
            />
          </div>
          {selectedChild && !isEditing && (
            <>
              {isMobile ? (
                <>
                  <IconButton
                    color="primary"
                    onClick={handleEditClick}
                    sx={{ marginRight: 1 }}
                    disabled={deletingChild}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={handleOpenDialog}
                    disabled={deletingChild}
                  >
                    {deletingChild ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEditClick}
                    sx={{ marginLeft: 1, textTransform: 'none' }}
                    disabled={deletingChild}
                  >
                    <EditIcon /> Modifier
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    sx={{ marginLeft: 1, textTransform: 'none' }}
                    onClick={handleOpenDialog}
                    disabled={deletingChild}
                    startIcon={deletingChild ? <CircularProgress size={16} /> : <DeleteIcon />}
                  >
                    {deletingChild ? 'Suppression...' : 'Supprimer'}
                  </Button>
                </>)}</>
          )}
        </Box>
        {isEditing ? (
          <Box mt={2}>
            <Typography variant="h6">Modifier les informations de l'enfant</Typography>
            <FormControl fullWidth variant="outlined" sx={{ marginBottom: 2 }}>
              <TextField
                id="name"
                variant="outlined"
                value={childDetails.name}
                onChange={(e) => setChildDetails({ ...childDetails, name: e.target.value })}
              />
            </FormControl>
            <FormControl fullWidth variant="outlined" sx={{ marginBottom: 2 }}>
              <TextField
                id="firstName"
                variant="outlined"
                value={childDetails.firstName}
                onChange={(e) => setChildDetails({ ...childDetails, firstName: e.target.value })}
                label="Prénom"
              />
            </FormControl>

            <FormControl fullWidth >
              <InputLabel>Classe</InputLabel>
              <Select
                value={childDetails.class}
                onChange={(e) => setChildDetails({ ...childDetails, class: e.target.value })}
                label="Classe"
              >
                <MenuItem value="PS/MS">PS/MS</MenuItem>
                <MenuItem value="GS/CP">GS/CP</MenuItem>
                <MenuItem value="CE1/CE2">CE1/CE2</MenuItem>
                <MenuItem value="CE2/CM1">CE2/CM1</MenuItem>
                <MenuItem value="CM1CM2">CM1/CM2</MenuItem>
                {/* Ajouter d'autres classes si nécessaire */}
              </Select>
            </FormControl>
            {/* Ajoutez d'autres champs selon les besoins */}
            <Box mt={2}>

              <Button
                variant="outlined"
                color="error"
                onClick={() => setIsEditing(false)}
                sx={{ marginRight: 2 }}
              >
                Annuler
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleEditSave}
              >
                Sauvegarder
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {selectedChild && (
              <>
                <Box mt={2}>
                  <Button
                    sx={{ marginRight: 2, marginTop: 1, textTransform: 'none' }}
                    variant="outlined"
                    color="secondary"
                    onClick={handleSelectAllDates}
                  >
                    Cocher tout le mois
                  </Button>
                  <Button
                    sx={{ textTransform: 'none', marginTop: 1 }}
                    variant="outlined"
                    color="warning"
                    onClick={handleSelectAllDatesOfYear}
                  >
                    Cocher toute l'année
                  </Button>
                </Box>

                <Box mt={1}>
                  <Calendar
                    locale={fr}
                    onChange={handleDateChange}
                    value={date}
                    onClickDay={toggleDateSelection}
                    onActiveStartDateChange={({ activeStartDate }) => {
                      handleMonthChange(activeStartDate);
                    }}
                    tileDisabled={({ date }) => !availableDates.some(availableDate => availableDate.toDateString() === date.toDateString())}
                    tileClassName={({ date, view }) => {
                      if (markedDates.some(markedDate => markedDate.toDateString() === date.toDateString())) {
                        return 'marked-date';
                      }
                      return '';
                    }}
                  />
                </Box>

                <Box mt={2}>
                  <Button
                    sx={{ marginRight: 2 }}
                    variant="outlined"
                    color="error"
                    onClick={handleRefresh}
                    disabled={!isEdited}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSave}
                  >
                    Sauvegarder
                  </Button>
                </Box>
              </>
            )}
          </>
        )}

        {/* Messages */}
        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </>
    );
  }, [
    loading, 
    children.length, 
    selectedChild, 
    isEditing, 
    childDetails, 
    handleChildAutocompleteChange, 
    handleEditClick, 
    handleOpenDialog, 
    handleEditSave, 
    date, 
    handleSelectAllDates, 
    handleSelectAllDatesOfYear, 
    markedDates, 
    availableDates, 
    toggleDateSelection, 
    handleMonthChange, 
    handleRefresh, 
    isEdited, 
    handleSave, 
    message, 
    error,
    isMobile,
    childrenOptions,
    selectedChildOption,
    deletingChild
  ]);


  return (
    <div>
      <Prompt when={isEdited} message='Vous avez des données non sauvegardées. En continuant, vous allez perdre ces données' />

      <Paper sx={{ padding: 1, marginTop: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ marginRight: 2, fontWeight: 'bold' }}>
            Inscription à la cantine
          </Typography>
          {/* Bouton de redirection vers la page profil */}
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
            component={Link}
            to="/profile"
          >
            Ajouter un enfant
          </Button>
        </Box>
        {renderContent}

      </Paper>
      <ConfirmationDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onConfirm={handleDeleteChild}
        title="Confirmation"
        message="Êtes-vous sûr de vouloir supprimer cet enfant ? Cette action est irréversible."
        loading={deletingChild}
      />
      <ConfirmationDialogDataNotSaved
        open={dialogOpen}
        onClose={handleCancelChange}
        onConfirm={handleConfirmChange}
      />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <Button color="inherit" onClick={handleSnackbarClose}>
            Fermer
          </Button>
        }
      />
    </div>
  );
};

export default CalendarPage;



